import { durationLabel } from "@/lib/utils";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, qrUrl } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { useEstablishment } from "@/context/EstablishmentContext";
import { useLang } from "@/context/LanguageContext";
import { formatMoney } from "@/lib/money";
import { buildPlans, regionForCountry } from "@/lib/plans";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import EstablishmentsMap from "@/components/EstablishmentsMap";
import {
  Building2, Plus, Pencil, Trash2, QrCode, MapPin, Package, PackageCheck, Bell,
  Crown, Lock, ArrowUpRight, Receipt, Layers, BarChart3, FileText, Rocket, Zap,
  Image as ImageIcon, Upload, Trash, Download, Printer,
} from "lucide-react";
import { fileToCompressedDataUrl } from "@/lib/image";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LabelList,
} from "recharts";
import { FindoSpinner } from "@/components/FindoSpinner";

export default function Establishments() {
  const { partner } = useAuth();
  const { t, lang } = useLang();
  const { establishments, load: reloadCtx, select } = useEstablishment();
  const [data, setData] = useState(null);      // consolidated
  const [billing, setBilling] = useState(null);
  const [limits, setLimits] = useState(null);
  const [dialog, setDialog] = useState(null);   // {mode:'create'|'edit', est?}
  const [confirmDel, setConfirmDel] = useState(null);
  const [qrEst, setQrEst] = useState(null);

  // Full self-service A4 poster (establishment logo + name, QR, findloop logo).
  const posterUrlFor = (est) =>
    `${qrUrl(partner.id).replace("/qr.png", "/selfservice-poster.pdf")}?lang=${lang}&e=${est.id}`;
  const posterPngFor = (est) =>
    `${qrUrl(partner.id).replace("/qr.png", "/selfservice-poster.png")}?lang=${lang}&e=${est.id}`;

  function printPoster(est) {
    if (!est) return;
    const url = posterPngFor(est);
    const w = window.open("", "_blank", "width=800,height=1000");
    if (!w) return;
    w.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Affiche findloop</title>
      <style>
        @page { size: A4; margin: 0; }
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        html, body { margin: 0; padding: 0; }
        img { width: 100%; height: auto; display: block; }
      </style></head><body>
        <img src="${url}" onload="setTimeout(function(){window.focus();window.print();},300)" onerror="window.print()" />
        <script>window.onafterprint = function(){ window.close(); };<\/script>
      </body></html>`);
    w.document.close();
  }

  const [imgEst, setImgEst] = useState(null);
  const [boostEst, setBoostEst] = useState(null);
  const [boostTiers, setBoostTiers] = useState([]);
  const [saving, setSaving] = useState(false);

  const isOwner = (partner?.role || "owner") === "owner";
  const canBoost = ["owner", "admin"].includes(partner?.role || "owner");
  const canManageImages = ["owner", "admin"].includes(partner?.role || "owner");
  const nowIso = new Date().toISOString();

  const load = useCallback(async () => {
    try {
      const [c, b, l] = await Promise.all([
        api("/establishments/consolidated"),
        api("/establishments/billing"),
        api("/establishments/limits"),
      ]);
      setData(c); setBilling(b); setLimits(l);
    } catch (e) { toast.error(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (canBoost) api("/establishments/boost-tiers").then((d) => setBoostTiers(d.tiers || d || [])).catch(() => {});
  }, [canBoost]);

  async function activateBoost(tier) {
    try {
      await api(`/establishments/${boostEst.id}/boost`, { method: "POST", body: { tier } });
      toast.success(t("est2.boostActivated").replace("{name}", boostEst.name));
      setBoostEst(null);
      await Promise.all([load(), reloadCtx()]);
    } catch (e) { toast.error(e.message); }
  }

  async function saveEstablishment(form) {
    setSaving(true);
    try {
      if (dialog.mode === "create") {
        await api("/establishments", { method: "POST", body: form });
        toast.success(t("est2.created"));
      } else {
        await api(`/establishments/${dialog.est.id}`, { method: "PATCH", body: form });
        toast.success(t("est2.updated"));
      }
      setDialog(null);
      await Promise.all([load(), reloadCtx()]);
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  }

  async function doDelete() {
    const est = confirmDel;
    setConfirmDel(null);
    try {
      await api(`/establishments/${est.id}`, { method: "DELETE" });
      toast.success(t("est2.deleted"));
      await Promise.all([load(), reloadCtx()]);
    } catch (e) { toast.error(e.message); }
  }

  const totals = data?.totals;
  const canAdd = limits?.can_add;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="establishments-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          title={t("est2.title")}
          subtitle={t("est2.subtitle")}
          testid="establishments-header"
        />
        {isOwner && (
          canAdd ? (
            <Button
              data-testid="add-establishment-btn"
              onClick={() => setDialog({ mode: "create" })}
              className="rounded-full bg-findo-indigo hover:bg-findo-indigo/90 font-semibold shrink-0"
            >
              <Plus size={17} className="mr-1.5" /> {t("est2.add")}
            </Button>
          ) : (
            <div className="shrink-0 text-right">
              <Button disabled data-testid="add-establishment-locked" className="rounded-full font-semibold cursor-not-allowed opacity-70">
                <Lock size={16} className="mr-1.5" /> {t("est2.limitReached")}
              </Button>
            </div>
          )
        )}
      </div>

      {/* Smart upgrade banner: recommend the right plan for the number of sites wanted */}
      {isOwner && !canAdd && (() => {
        const wanted = (limits?.current ?? 1) + 1;
        const recKey = wanted >= 3 ? "medium" : "small";
        const region = regionForCountry(partner?.country);
        const rec = buildPlans(region, lang).find((p) => p.key === recKey) || {};
        const goUpgrade = () => { try { localStorage.setItem("findo_plan_tier", recKey); } catch { /* ignore */ } };
        return (
          <div data-testid="establishment-upgrade-banner"
            className="mt-2 mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-5 flex items-start gap-4">
            <div className="rounded-xl bg-amber-100 p-2.5 text-amber-700"><Crown size={22} /></div>
            <div className="flex-1">
              <p className="font-display font-bold text-amber-900" data-testid="upgrade-banner-title">
                {t("hc.limitReachedUpgradeToA")}
              </p>
              <p className="text-sm text-amber-800 mt-0.5">
                {t("hc.planLimitFmt").replace("{plan}", buildPlans(regionForCountry(partner?.country), lang).find((p) => p.key === limits?.tier)?.name || limits?.tier_name || "").replace("{max}", limits?.max ?? "").replace("{n}", wanted)}{" "}
                <b data-testid="upgrade-reco-name">{rec.name || (recKey === "medium" ? "Business" : "Starter")}</b>
                {rec.price ? <> {t("hc.planAt")} <b data-testid="upgrade-reco-price">{rec.price}{rec.sub}</b></> : null}.
              </p>
              <Link to="/b2b/account" onClick={goUpgrade}
                className="inline-flex items-center gap-1.5 mt-3 rounded-full bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 text-sm font-semibold transition-colors"
                data-testid="upgrade-cta">
                {t("hc.switchTo")} {rec.name || (recKey === "medium" ? "Business" : "Starter")} <ArrowUpRight size={15} />
              </Link>
            </div>
          </div>
        );
      })()}

      {/* Consolidated overview */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="consolidated-overview">
        <Kpi Icon={Building2} label={t("est2.title")} value={totals?.establishments ?? "—"} />
        <Kpi Icon={Package} label={t("est2.kItemsTotal")} value={totals?.items_total ?? "—"} />
        <Kpi Icon={Layers} label={t("est2.kInStorage")} value={totals?.items_in_storage ?? "—"} />
        <Kpi Icon={PackageCheck} label={t("est2.kRestituted")} value={totals?.items_restituted ?? "—"} />
        <Kpi Icon={Bell} label={t("est2.kDeclarations")} value={totals?.declarations_total ?? "—"} />
      </div>

      {/* Per-establishment restitution rate comparison */}
      <RestitutionChart establishments={data?.establishments || []} />

      {/* Geographic map of all sites, coloured by restitution rate */}
      <EstablishmentsMap establishments={data?.establishments || []} />

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        {/* Establishment cards */}
        <div className="lg:col-span-2 space-y-4" data-testid="establishment-list">
          {(data?.establishments || []).map((e) => (
            <div key={e.id} className="bg-white rounded-2xl border border-border p-5 card-shadow" data-testid={`establishment-card-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-bold text-slate-900 truncate">{e.name}</h3>
                    {e.is_primary && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-findo-indigo/10 text-findo-indigo text-[11px] font-bold px-2 py-0.5">
                        <Crown size={11} /> {t("est2.primary")}
                      </span>
                    )}
                    {!e.active && (
                      <span className="rounded-full bg-red-100 text-red-700 text-[11px] font-bold px-2 py-0.5">{t("est2.suspended")}</span>
                    )}
                    {e.boost_until && e.boost_until > nowIso && (
                      <span className="inline-flex items-center gap-1 boost-shine rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold px-2 py-0.5" data-testid={`est-boosted-${e.id}`}>
                        <Zap size={11} className="fill-amber-500" /> {t("est2.boosted")}
                      </span>
                    )}
                  </div>
                  {(e.address || e.city) && (
                    <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5">
                      <MapPin size={13} /> {[e.address, e.city, e.country].filter(Boolean).join(", ")}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button title={t("est2.qr")} data-testid={`est-qr-${e.id}`} onClick={() => setQrEst(e)}
                    className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><QrCode size={17} /></button>
                  {canManageImages && (
                    <button title={t("est2.visuals")} data-testid={`est-images-${e.id}`} onClick={() => setImgEst(e)}
                      className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><ImageIcon size={17} /></button>
                  )}
                  {isOwner && (
                    <>
                      <button title={t("est2.edit")} data-testid={`est-edit-${e.id}`} onClick={() => setDialog({ mode: "edit", est: e })}
                        className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><Pencil size={16} /></button>
                      {!e.is_primary && (
                        <button title={t("est2.delete")} data-testid={`est-delete-${e.id}`} onClick={() => setConfirmDel(e)}
                          className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                <MiniStat label={t("est2.mItems")} value={e.items_total} />
                <MiniStat label={t("est2.mStorage")} value={e.items_in_storage} />
                <MiniStat label={t("est2.mRestituted")} value={e.items_restituted} />
                <MiniStat label={t("est2.mDeclar")} value={e.declarations_total} />
              </div>
              <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
                <button
                  data-testid={`est-open-${e.id}`}
                  onClick={() => { select(e.id); toast.success(t("est2.working").replace("{name}", e.name)); }}
                  className="text-sm font-semibold text-findo-indigo hover:underline inline-flex items-center gap-1"
                >
                  {t("est2.workOn")} <ArrowUpRight size={14} />
                </button>
                {canBoost && e.active && (
                  <Button
                    size="sm"
                    onClick={() => setBoostEst(e)}
                    data-testid={`est-boost-btn-${e.id}`}
                    className="rounded-full font-semibold bg-amber-500 hover:bg-amber-600 text-white h-8"
                  >
                    <Rocket size={14} className="mr-1.5" />
                    {e.boost_until && e.boost_until > nowIso ? t("est2.boostExtend") : t("est2.boostCta")}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Billing breakdown */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-border p-5 card-shadow" data-testid="billing-card">
            <div className="flex items-center gap-2 mb-1">
              <Receipt size={18} className="text-findo-indigo" />
              <h3 className="font-display font-bold text-slate-900">{t("est2.billing")}</h3>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              {t("est2.plan")} {buildPlans(regionForCountry(partner?.country), lang).find((p) => p.key === billing?.tier)?.name || billing?.tier_name}
            </p>
            <div className="space-y-2">
              <Row label={t("est2.baseSub")} value={fmtEur(billing?.base_subscription_eur, lang)} />
              {(billing?.establishments || []).filter((r) => !r.included).map((r) => (
                <Row key={r.id} label={`+ ${r.name}`} value={r.on_quote ? t("est2.onQuote") : fmtEur(r.monthly_eur, lang)} sub />
              ))}
              <div className="border-t border-border pt-2 mt-2 flex items-center justify-between">
                <span className="font-display font-bold text-slate-900">{t("est2.monthlyTotal")}</span>
                <span className="font-display font-bold text-findo-indigo text-lg" data-testid="billing-total">
                  {fmtEur(billing?.monthly_total_eur, lang)}
                </span>
              </div>
              {billing?.extra_count > 0 && (
                <p className="text-xs text-slate-400" data-testid="billing-extra-note">
                  {t("est2.extraNoteBase").replace("{n}", billing.extra_count)}{billing.on_quote ? t("est2.extraQuote") : t("est2.extraPrice").replace("{price}", billing.extra_unit_price_eur)}
                </p>
              )}
              <Link to="/b2b/invoices" className="mt-2 flex items-center justify-center gap-1.5 rounded-xl bg-findo-indigo/5 hover:bg-findo-indigo/10 border border-findo-indigo/15 text-findo-indigo text-xs font-semibold py-2 transition-colors" data-testid="billing-invoices-link">
                <FileText size={13} /> {t("est2.seeInvoices")}
              </Link>
              <p className="text-[11px] text-slate-400 pt-1">{t("est2.autoInvoice")}</p>
            </div>
          </div>

          <div className="bg-findo-indigo/5 rounded-2xl border border-findo-indigo/15 p-5" data-testid="plan-summary">
            <p className="text-sm font-semibold text-findo-indigo flex items-center gap-1.5">
              <Building2 size={15} /> {limits?.current ?? "—"}
              {limits?.max ? ` / ${limits.max}` : (limits?.unlimited ? t("est2.unlimitedSuffix") : "")}{t("est2.estSuffix")}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {limits?.unlimited
                ? t("est2.unlimitedPlan")
                : t("est2.includedPlan").replace("{n}", limits?.included ?? 1)}
            </p>
          </div>
        </div>
      </div>

      {/* Create / edit dialog */}
      {dialog && (
        <EstablishmentDialog
          mode={dialog.mode}
          est={dialog.est}
          saving={saving}
          onClose={() => setDialog(null)}
          onSave={saveEstablishment}
        />
      )}

      {/* QR + poster preview dialog */}
      <Dialog open={!!qrEst} onOpenChange={(o) => !o && setQrEst(null)}>
        <DialogContent className="max-w-2xl" data-testid="est-qr-dialog">
          <DialogHeader>
            <DialogTitle>{t("est2.qrTitle").replace("{name}", qrEst?.name || "")}</DialogTitle>
            <DialogDescription>
              {t("hc.previewOfThePrintReady")}
            </DialogDescription>
          </DialogHeader>
          {qrEst && (
            <div className="flex flex-col items-center py-1">
              {/* Live preview of the real A4 self-service poster (rendered as PNG) */}
              <div className="w-full rounded-xl border border-border overflow-hidden bg-slate-100 flex items-center justify-center p-3" style={{ height: "64vh" }}>
                <img
                  key={qrEst.id}
                  src={posterPngFor(qrEst)}
                  alt={t("hc.posterPreview")}
                  className="max-h-full max-w-full w-auto h-auto object-contain shadow-lg rounded-sm bg-white"
                  data-testid="est-poster-preview"
                />
              </div>
              <div className="mt-4 flex items-center gap-2 flex-wrap justify-center">
                <Button onClick={() => printPoster(qrEst)} className="rounded-xl font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="est-poster-print">
                  <Printer size={15} className="mr-1.5" /> {t("hc.printThePoster")}
                </Button>
                <a
                  href={posterUrlFor(qrEst)}
                  target="_blank" rel="noreferrer"
                  data-testid="est-poster-download"
                >
                  <Button variant="outline" className="rounded-xl font-semibold"><FileText size={15} className="mr-1.5" /> {t("stg.posterPdf")}</Button>
                </a>
                <a
                  href={`${qrUrl(partner.id)}?e=${qrEst.id}`}
                  download={`findloop-qr-${(qrEst.name || qrEst.id).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`}
                  data-testid="est-qr-download"
                >
                  <Button variant="outline" className="rounded-xl font-semibold"><Download size={15} className="mr-1.5" /> {t("stg.qrDownload")}</Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Establishment visuals (logo + cover) dialog — owner/admin only */}
      <EstablishmentImagesDialog
        est={imgEst}
        onClose={() => setImgEst(null)}
        onSaved={async () => { await Promise.all([load(), reloadCtx()]); }}
      />

      {/* Boost dialog */}
      <Dialog open={!!boostEst} onOpenChange={(o) => !o && setBoostEst(null)}>
        <DialogContent data-testid="est-boost-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket size={18} className="text-amber-500" /> {t("est2.boostTitle").replace("{name}", boostEst?.name || "")}
            </DialogTitle>
            <DialogDescription>{t("est2.boostDesc")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            {(boostTiers || []).map((tr) => (
              <button
                key={tr.key || tr.tier}
                onClick={() => activateBoost(tr.key || tr.tier)}
                data-testid={`boost-tier-${tr.key || tr.tier}`}
                className="flex items-center justify-between rounded-xl border border-border hover:border-amber-400 hover:bg-amber-50 p-4 transition-colors text-left"
              >
                <div>
                  <p className="font-display font-bold text-slate-900">{tr.hours ? durationLabel(tr.hours, lang) : (tr.label_fr || tr.label || tr.key)}</p>
                  <p className="text-xs text-slate-500">{t("est2.boostDuration").replace("{h}", tr.hours)}</p>
                </div>
                <span className="font-display font-bold text-amber-600 text-lg" data-testid={`boost-tier-price-${tr.key || tr.tier}`}>{fmtEur(tr.price_eur, lang)} <span className="text-xs font-semibold text-slate-500">{t("price.ht")}</span></span>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 pt-1">{t("est2.boostBillingNote")}</p>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="est-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("est2.deleteTitle").replace("{name}", confirmDel?.name || "")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("est2.deleteDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="est-delete-cancel">{t("c.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="est-delete-confirm" className="bg-red-600 hover:bg-red-700">
              {t("est2.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EstablishmentDialog({ mode, est, saving, onClose, onSave }) {
  const { t } = useLang();
  const [name, setName] = useState(est?.name || "");
  const [address, setAddress] = useState(est?.address || "");
  const [city, setCity] = useState(est?.city || "");
  const [streetNumber, setStreetNumber] = useState(est?.street_number || "");
  const [streetName, setStreetName] = useState(est?.street_name || "");
  const [postalCode, setPostalCode] = useState(est?.postal_code || "");
  const [country, setCountry] = useState(est?.country || "FR");
  const [email, setEmail] = useState(est?.email || "");
  const [phone, setPhone] = useState(est?.phone || "");

  function submit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), address: address.trim(), city: city.trim(),
             street_number: streetNumber.trim(), street_name: streetName.trim(), postal_code: postalCode.trim(),
             country: country.trim().toUpperCase(), email: email.trim(), phone: phone.trim() });
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="establishment-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? t("est2.newEst") : t("est2.editEst")}</DialogTitle>
          <DialogDescription>{t("est2.dialogDesc")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="findo-numbered space-y-5">
          <div>
            <Label className="mb-1.5 block">{t("est2.nameLabel")}</Label>
            <Input data-testid="est-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("est2.namePh")} required />
          </div>
          <div>
            <Label className="mb-1.5 block">{t("est2.addressLabel")}</Label>
            <Input data-testid="est-address-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder={t("est2.addressPh")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">{t("est2.cityLabel")}</Label>
              <Input data-testid="est-city-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder={t("est2.cityPh")} />
            </div>
            <div>
              <Label className="mb-1.5 block">{t("est2.countryLabel")}</Label>
              <Input data-testid="est-country-input" value={country} onChange={(e) => setCountry(e.target.value)} maxLength={2} placeholder="FR" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="mb-1.5 block text-xs text-slate-400">{t("hc.streetNo")} <span className="text-slate-300">{t("hc.optional2")}</span></Label>
              <Input data-testid="est-street-number-input" value={streetNumber} onChange={(e) => setStreetNumber(e.target.value)} placeholder="12" />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs text-slate-400">{t("hc.streetName")} <span className="text-slate-300">{t("hc.optional2")}</span></Label>
              <Input data-testid="est-street-name-input" value={streetName} onChange={(e) => setStreetName(e.target.value)} placeholder={t("hc.mainStreet")} />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs text-slate-400">{t("pay.postal")} <span className="text-slate-300">{t("hc.optional2")}</span></Label>
              <Input data-testid="est-postal-code-input" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="75001" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">{t("est2.emailLabel")}</Label>
              <Input data-testid="est-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("est2.emailPh")} />
            </div>
            <div>
              <Label className="mb-1.5 block">{t("est2.phoneLabel")}</Label>
              <Input data-testid="est-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("est2.phonePh")} />
            </div>
          </div>
          <p className="text-xs text-slate-400 -mt-1">{t("est2.contactHint")}</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full">{t("c.cancel")}</Button>
            <Button type="submit" disabled={saving || !name.trim()} data-testid="est-save-btn" className="rounded-full bg-findo-indigo hover:bg-findo-indigo/90 font-semibold">
              {saving ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("est2.saving")}</span> : (mode === "create" ? t("est2.create") : t("c.save"))}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EstablishmentImagesDialog({ est, onClose, onSaved }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(null);
  const [ver, setVer] = useState(0);
  const [logo, setLogo] = useState(null);
  const [cover, setCover] = useState(null);
  const logoRef = useRef(null);
  const coverRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (est) { setLogo(est.logo_url || null); setCover(est.cover_url || null); setVer(0); }
  }, [est]);

  async function upload(kind, file) {
    if (!file) return;
    setBusy(kind);
    try {
      const b64 = await fileToCompressedDataUrl(file, { maxDim: kind === "cover" ? 1600 : 512, quality: 0.85 });
      await api(`/establishments/${est.id}/${kind}`, { method: "PUT", body: { image_base64: b64 } });
      const url = `/api/establishments/${est.id}/${kind}.jpg`;
      if (kind === "logo") setLogo(url); else setCover(url);
      setVer((v) => v + 1);
      toast.success(t("est2.imgSaved"));
      await onSaved?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  }

  async function remove(kind) {
    setBusy(kind);
    try {
      await api(`/establishments/${est.id}/${kind}`, { method: "DELETE" });
      if (kind === "logo") setLogo(null); else setCover(null);
      toast.success(t("est2.imgRemoved"));
      await onSaved?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  }

  const logoSrc = logo ? `${BACKEND}${logo}?v=${ver}` : null;
  const coverSrc = cover ? `${BACKEND}${cover}?v=${ver}` : null;

  return (
    <Dialog open={!!est} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="est-images-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon size={18} className="text-findo-indigo" /> {t("est2.visualsTitle").replace("{name}", est?.name || "")}
          </DialogTitle>
          <DialogDescription>{t("est2.visualsDesc")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Cover */}
          <div>
            <Label className="mb-2 block">{t("est2.coverLabel")}</Label>
            <div className="relative h-32 rounded-xl overflow-hidden border border-border bg-slate-100">
              {coverSrc
                ? <img src={coverSrc} alt="" className="w-full h-full object-cover" data-testid="est-cover-preview" />
                : <div className="w-full h-full grid place-items-center text-slate-300"><ImageIcon size={30} /></div>}
            </div>
            <div className="mt-2 flex gap-2">
              <Button type="button" size="sm" variant="outline" disabled={busy === "cover"} onClick={() => coverRef.current?.click()}
                className="rounded-full" data-testid="est-cover-upload">
                <Upload size={14} className="mr-1.5" /> {busy === "cover" ? t("est2.imgUploading") : t("est2.imgChange")}
              </Button>
              {cover && (
                <Button type="button" size="sm" variant="ghost" disabled={busy === "cover"} onClick={() => remove("cover")}
                  className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="est-cover-remove">
                  <Trash size={14} className="mr-1.5" /> {t("est2.imgRemove")}
                </Button>
              )}
            </div>
            <input ref={coverRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload("cover", f); }} />
          </div>

          {/* Logo */}
          <div>
            <Label className="mb-2 block">{t("est2.logoLabel")}</Label>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-2xl overflow-hidden border border-border bg-slate-100 grid place-items-center shrink-0">
                {logoSrc
                  ? <img src={logoSrc} alt="" className="w-full h-full object-cover" data-testid="est-logo-preview" />
                  : <ImageIcon size={26} className="text-slate-300" />}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button type="button" size="sm" variant="outline" disabled={busy === "logo"} onClick={() => logoRef.current?.click()}
                  className="rounded-full" data-testid="est-logo-upload">
                  <Upload size={14} className="mr-1.5" /> {busy === "logo" ? t("est2.imgUploading") : t("est2.imgChange")}
                </Button>
                {logo && (
                  <Button type="button" size="sm" variant="ghost" disabled={busy === "logo"} onClick={() => remove("logo")}
                    className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="est-logo-remove">
                    <Trash size={14} className="mr-1.5" /> {t("est2.imgRemove")}
                  </Button>
                )}
              </div>
            </div>
            <input ref={logoRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload("logo", f); }} />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" onClick={onClose} className="rounded-full bg-findo-indigo hover:bg-findo-indigo/90 font-semibold" data-testid="est-images-done">
            {t("est2.imgDone")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function rateColor(rate) {
  // Continuous red→green by restitution rate (hsl 0→140).
  const h = Math.round((Math.max(0, Math.min(100, rate)) / 100) * 140);
  return `hsl(${h}, 70%, 45%)`;
}

function RestitutionChart({ establishments }) {
  const { t } = useLang();
  const rows = (establishments || [])
    .filter((e) => (e.items_total || 0) > 0)
    .map((e) => ({
      name: e.name.length > 18 ? e.name.slice(0, 17) + "…" : e.name,
      rate: Math.round(((e.items_restituted || 0) / e.items_total) * 100),
      restituted: e.items_restituted || 0,
      total: e.items_total || 0,
    }));

  if (rows.length < 1) return null;

  return (
    <div className="mt-6 bg-white rounded-2xl border border-border p-5 card-shadow" data-testid="restitution-chart">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 size={18} className="text-findo-indigo" />
        <h3 className="font-display font-bold text-slate-900">{t("est2.chartTitle")}</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">{t("est2.chartDesc")}</p>
      <ResponsiveContainer width="100%" height={Math.max(160, rows.length * 46)}>
        <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 24 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef0f4" />
          <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 12, fill: "#94a3b8" }} />
          <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12, fill: "#475569" }} />
          <Tooltip
            formatter={(v, _n, p) => [`${v}% (${p.payload.restituted}/${p.payload.total})`, t("est2.chartTooltip")]}
            contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }}
          />
          <Bar dataKey="rate" radius={[0, 6, 6, 0]} minPointSize={3} data-testid="restitution-bar">
            {rows.map((r, i) => <Cell key={i} fill={rateColor(r.rate)} />)}
            <LabelList dataKey="rate" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 12, fontWeight: 700, fill: "#475569" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Kpi({ Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-4 card-shadow">
      <div className="flex items-center gap-2 text-slate-400"><Icon size={16} /><span className="text-xs font-semibold">{label}</span></div>
      <p className="mt-1.5 font-display font-bold text-2xl text-slate-900">{value}</p>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl bg-slate-50 py-2">
      <p className="font-display font-bold text-slate-900">{value ?? 0}</p>
      <p className="text-[11px] text-slate-400 font-semibold">{label}</p>
    </div>
  );
}

function Row({ label, value, sub }) {
  return (
    <div className={`flex items-center justify-between ${sub ? "text-sm text-slate-500" : "text-sm text-slate-700"}`}>
      <span className="truncate pr-2">{label}</span>
      <span className="font-semibold whitespace-nowrap">{value}</span>
    </div>
  );
}

function fmtEur(v, lang = "fr") {
  if (v == null) return "—";
  const n = Number(v);
  return formatMoney(n, lang, "EUR", { decimals: Number.isInteger(n) ? 0 : 2 });
}
